// /admin/students/review — manual verification queue for students whose
// school couldn't be auto-resolved from their email (self_attested or
// unmatched). Also surfaces the university_requests pipeline from
// Patch 9 in a second tab.
//
// Access: super_admin / admin_licensing / admin_rm. campus_admin is not
// granted access — their portal scopes them to their own school via the
// existing (campus)/ surface; verification across schools is engagement-
// tier work.

import type { Href } from "expo-router";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";

import { useAuth } from "../../../../lib/authContext";
import { logAdminAction } from "../../../../lib/auditLog";
import {
  adminRejectStudentSchool,
  adminVerifyStudentSchool,
  approveUniversityRequest,
  bulkReactivateByState,
  deactivateUniversity,
  listStudentsForReview,
  listUniversities,
  listUniversityDirectory,
  listUniversityRequests,
  listUniversityStateBreakdown,
  mergeUniversityInto,
  reactivateUniversity,
  rejectUniversityRequest,
  updateUniversityRow,
  type DirectoryFilters,
  type DirectoryUniversity,
  type StudentVerificationRow,
  type University,
  type UniversityRequest,
} from "../../../../lib/universities";
import { COLORS, FONTS } from "../../../../lib/brand";
import { PANE_THEMES } from "../../../../components/brand/paneThemes";
import {
  AdminBanner,
  AdminButton,
  AdminEmpty,
  AdminError,
  AdminLoading,
  AdminPageHeader,
  FilterChip as SharedFilterChip,
} from "../../../../components/admin/ui";

const THEME = PANE_THEMES.Admin;

const BLACK = THEME.pageBg;
const FOREST = THEME.sidebarBg;
const FOREST_MID = THEME.surfaceRaisedBg;
const AMBER = THEME.accent;
const SAGE = COLORS.SAGE.hex;
const LINEN = THEME.textOnSurface;
const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const RED = THEME.destructive;
const BORDER = "rgba(255,255,255,0.07)";

void FOREST;

type Tab = "students" | "requests" | "directory";
type StudentFilter = "all" | "self_attested" | "no_school";

export default function AdminStudentsReviewScreen() {
  const { role } = useAuth();
  const canAccess = role === "super_admin" || role === "admin_licensing" || role === "admin_rm";

  const [tab, setTab] = useState<Tab>("students");
  const [studentFilter, setStudentFilter] = useState<StudentFilter>("all");
  const [students, setStudents] = useState<StudentVerificationRow[]>([]);
  const [requests, setRequests] = useState<UniversityRequest[]>([]);
  const [universities, setUniversities] = useState<University[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    setErr(null);
    try {
      const [s, r, u] = await Promise.all([
        listStudentsForReview(),
        listUniversityRequests(),
        listUniversities(),
      ]);
      setStudents(s);
      setRequests(r);
      setUniversities(u);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  const visibleStudents = useMemo(() => {
    return students.filter((s) => {
      if (s.school_verified) return false; // queue is "needs review"
      if (studentFilter === "self_attested") return s.school_verification_method === "self_attested";
      if (studentFilter === "no_school")
        return s.school_verification_method == null && !s.university_id && !s.university;
      return true;
    });
  }, [students, studentFilter]);

  const pendingRequests = useMemo(() => requests.filter((r) => r.status === "pending"), [requests]);

  if (!canAccess) {
    return (
      <ScrollView style={{ flex: 1, backgroundColor: BLACK }}>
        <View style={{ padding: 24 }}>
          <AdminBanner
            theme={THEME}
            tone="error"
            title="Access denied"
            message="Student verification review is restricted to super_admin / admin_licensing / admin_rm."
          />
        </View>
      </ScrollView>
    );
  }

  return (
    <ScrollView style={{ flex: 1, backgroundColor: BLACK }} contentContainerStyle={{ paddingBottom: 64 }}>
      <View style={{ padding: 24, maxWidth: 1080 }}>
        <AdminPageHeader
          theme={THEME}
          eyebrow="People · Students · Verification"
          title="Student verification review"
          subtitle={`${visibleStudents.length} student${visibleStudents.length === 1 ? "" : "s"} awaiting school verification${pendingRequests.length > 0 ? ` · ${pendingRequests.length} school request${pendingRequests.length === 1 ? "" : "s"} pending` : ""}`}
        />

        {err ? <ErrorBanner>{err}</ErrorBanner> : null}

        {/* Tabs */}
        <View style={{ marginTop: 18, flexDirection: "row", gap: 4, borderBottomWidth: 1, borderBottomColor: BORDER, flexWrap: "wrap" }}>
          <TabButton label="Students" active={tab === "students"} onPress={() => setTab("students")} badge={visibleStudents.length} />
          <TabButton label="School requests" active={tab === "requests"} onPress={() => setTab("requests")} badge={pendingRequests.length} />
          <TabButton label="School directory" active={tab === "directory"} onPress={() => setTab("directory")} />
        </View>

        {tab === "directory" ? (
          <SchoolDirectoryTab />
        ) : tab === "students" ? (
          <>
            {/* Filter chips */}
            <View style={{ marginTop: 14, flexDirection: "row", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: WM }}>Filter:</Text>
              <FilterChip label="All unverified" active={studentFilter === "all"} onPress={() => setStudentFilter("all")} />
              <FilterChip label="Self-attested" active={studentFilter === "self_attested"} onPress={() => setStudentFilter("self_attested")} />
              <FilterChip label="No school set" active={studentFilter === "no_school"} onPress={() => setStudentFilter("no_school")} />
            </View>

            {loading ? (
              <AdminLoading theme={THEME} />
            ) : visibleStudents.length === 0 ? (
              <EmptyState
                title="All students verified."
                body="Nice work. Newly-signed-up students whose emails don't match a partner school will surface here for manual review."
              />
            ) : (
              <View style={{ marginTop: 14, gap: 8 }}>
                {visibleStudents.map((row) => (
                  <StudentReviewRow
                    key={row.id}
                    row={row}
                    universities={universities}
                    onAction={() => void load()}
                  />
                ))}
              </View>
            )}
          </>
        ) : (
          <>
            {loading ? (
              <AdminLoading theme={THEME} />
            ) : pendingRequests.length === 0 ? (
              <EmptyState
                title="No school requests."
                body="When a student picks &quot;My school isn't listed&quot; from the signup picker, their request appears here for approval."
              />
            ) : (
              <View style={{ marginTop: 14, gap: 8 }}>
                {pendingRequests.map((req) => (
                  <UniversityRequestRow key={req.id} req={req} onAction={() => void load()} />
                ))}
              </View>
            )}
            {/* Past requests collapsed view */}
            {requests.filter((r) => r.status !== "pending").length > 0 ? (
              <View style={{ marginTop: 30 }}>
                <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9.5, letterSpacing: 1.6, textTransform: "uppercase", color: WM, marginBottom: 8 }}>
                  Recently decided
                </Text>
                {requests
                  .filter((r) => r.status !== "pending")
                  .slice(0, 10)
                  .map((r) => (
                    <View key={r.id} style={{ padding: 10, borderRadius: 4, backgroundColor: FOREST_MID, borderWidth: 1, borderColor: BORDER, marginBottom: 6 }}>
                      <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: LINEN }}>
                        {r.school_name} · <Text style={{ color: r.status === "approved" ? SAGE : RED }}>{r.status}</Text>
                      </Text>
                      <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11, color: WM }}>
                        Requested by {r.requester_name ?? r.requester_email ?? "—"} · {new Date(r.created_at).toLocaleDateString()}
                        {r.review_reason ? ` · "${r.review_reason}"` : ""}
                      </Text>
                    </View>
                  ))}
              </View>
            ) : null}
          </>
        )}
      </View>
    </ScrollView>
  );
}

// ─── School directory tab ──────────────────────────────────────────────

const USPS_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC","PR",
];

function SchoolDirectoryTab() {
  const [rows, setRows] = useState<DirectoryUniversity[]>([]);
  const [total, setTotal] = useState(0);
  const [stateBreakdown, setStateBreakdown] = useState<Array<{ state_code: string | null; n: number }>>([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [filters, setFilters] = useState<DirectoryFilters>({
    search: "",
    state: null,
    isActive: true,
    hasEmailDomain: null,
  });
  const [editTarget, setEditTarget] = useState<DirectoryUniversity | null>(null);
  const [mergeSource, setMergeSource] = useState<DirectoryUniversity | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [pageData, br] = await Promise.all([
        listUniversityDirectory(page, filters),
        listUniversityStateBreakdown(),
      ]);
      setRows(pageData.rows);
      setTotal(pageData.total);
      setStateBreakdown(br);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Directory load failed");
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function onBulkFLReactivate() {
    try {
      const n = await bulkReactivateByState("FL");
      void logAdminAction({
        action: "university.bulk_reactivate",
        entityType: "universities",
        summary: `Bulk-reactivated ${n} inactive Florida schools`,
      });
      await reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Bulk reactivate failed");
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / 50));
  const top10States = stateBreakdown.filter((b) => b.state_code).slice(0, 10);

  return (
    <View style={{ marginTop: 14 }}>
      {/* Stats strip */}
      <View
        style={{
          padding: 12,
          borderRadius: 6,
          backgroundColor: FOREST_MID,
          borderWidth: 1,
          borderColor: BORDER,
        }}
      >
        <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 10, letterSpacing: 1.4, textTransform: "uppercase", color: WM }}>
          Universities · US directory
        </Text>
        <Text style={{ marginTop: 4, fontFamily: FONTS.bodyMedium, fontSize: 13.5, color: LINEN }}>
          {total} schools match current filters · {stateBreakdown.reduce((s, b) => s + b.n, 0)} active US total
        </Text>
        {top10States.length > 0 ? (
          <Text style={{ marginTop: 6, fontFamily: FONTS.body, fontSize: 11.5, color: WS }}>
            Top states:{" "}
            {top10States.map((b, i) => (
              <Text key={b.state_code ?? `_${i}`}>
                {b.state_code}={b.n}
                {i < top10States.length - 1 ? " · " : ""}
              </Text>
            ))}
          </Text>
        ) : null}
      </View>

      {/* Filters */}
      <View style={{ marginTop: 12, gap: 8 }}>
        <TextInput
          value={filters.search ?? ""}
          onChangeText={(v) => {
            setPage(0);
            setFilters((f) => ({ ...f, search: v }));
          }}
          placeholder="Search by name or email domain…"
          placeholderTextColor={WL}
          style={{
            backgroundColor: BLACK,
            borderWidth: 1,
            borderColor: BORDER,
            borderRadius: 4,
            paddingHorizontal: 12,
            paddingVertical: 9,
            color: W,
            fontFamily: FONTS.body,
            fontSize: 13,
            ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
          } as unknown as TextStyle}
        />
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: WM }}>State:</Text>
          <FilterChip label="Any" active={!filters.state} onPress={() => { setPage(0); setFilters((f) => ({ ...f, state: null })); }} />
          <FilterChip label="FL" active={filters.state === "FL"} onPress={() => { setPage(0); setFilters((f) => ({ ...f, state: "FL" })); }} />
          <FilterChip label="CA" active={filters.state === "CA"} onPress={() => { setPage(0); setFilters((f) => ({ ...f, state: "CA" })); }} />
          <FilterChip label="NY" active={filters.state === "NY"} onPress={() => { setPage(0); setFilters((f) => ({ ...f, state: "NY" })); }} />
          <FilterChip label="TX" active={filters.state === "TX"} onPress={() => { setPage(0); setFilters((f) => ({ ...f, state: "TX" })); }} />
          <FilterChip label="GA" active={filters.state === "GA"} onPress={() => { setPage(0); setFilters((f) => ({ ...f, state: "GA" })); }} />
          <FilterChip label="NC" active={filters.state === "NC"} onPress={() => { setPage(0); setFilters((f) => ({ ...f, state: "NC" })); }} />
        </View>
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: WM }}>Status:</Text>
          <FilterChip label="Active only" active={filters.isActive === true} onPress={() => { setPage(0); setFilters((f) => ({ ...f, isActive: true })); }} />
          <FilterChip label="Inactive only" active={filters.isActive === false} onPress={() => { setPage(0); setFilters((f) => ({ ...f, isActive: false })); }} />
          <FilterChip label="All" active={filters.isActive == null} onPress={() => { setPage(0); setFilters((f) => ({ ...f, isActive: null })); }} />
          <Text style={{ marginLeft: 12, fontFamily: FONTS.bodyMedium, fontSize: 11, color: WM }}>Email domain:</Text>
          <FilterChip label="Any" active={filters.hasEmailDomain == null} onPress={() => { setPage(0); setFilters((f) => ({ ...f, hasEmailDomain: null })); }} />
          <FilterChip label="Has domain" active={filters.hasEmailDomain === true} onPress={() => { setPage(0); setFilters((f) => ({ ...f, hasEmailDomain: true })); }} />
          <FilterChip label="No domain" active={filters.hasEmailDomain === false} onPress={() => { setPage(0); setFilters((f) => ({ ...f, hasEmailDomain: false })); }} />
        </View>
        <View>
          <SmallBtn label="Bulk reactivate inactive Florida schools" onPress={onBulkFLReactivate} tint={AMBER} />
        </View>
      </View>

      {err ? <ErrorBanner>{err}</ErrorBanner> : null}

      {loading ? (
        <AdminLoading theme={THEME} />
      ) : rows.length === 0 ? (
        <EmptyState title="No schools match." body="Try removing a filter or widening the search." />
      ) : (
        <View style={{ marginTop: 14, gap: 6 }}>
          {rows.map((row) => (
            <DirectoryRow
              key={row.id}
              row={row}
              onEdit={() => setEditTarget(row)}
              onDeactivate={async () => {
                try {
                  await deactivateUniversity(row.id);
                  void logAdminAction({
                    action: "university.deactivate",
                    entityType: "universities",
                    entityId: row.id,
                    summary: `Deactivated ${row.name}`,
                  });
                  await reload();
                } catch (e) {
                  setErr(e instanceof Error ? e.message : "Deactivate failed");
                }
              }}
              onReactivate={async () => {
                try {
                  await reactivateUniversity(row.id);
                  void logAdminAction({
                    action: "university.reactivate",
                    entityType: "universities",
                    entityId: row.id,
                    summary: `Reactivated ${row.name}`,
                  });
                  await reload();
                } catch (e) {
                  setErr(e instanceof Error ? e.message : "Reactivate failed");
                }
              }}
              onMerge={() => setMergeSource(row)}
            />
          ))}
        </View>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 ? (
        <View style={{ marginTop: 14, flexDirection: "row", alignItems: "center", gap: 10 }}>
          <SmallBtn label="← Prev" onPress={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} />
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: WS }}>
            Page {page + 1} of {totalPages}
          </Text>
          <SmallBtn label="Next →" onPress={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} />
        </View>
      ) : null}

      {editTarget ? (
        <EditUniversityModal
          row={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={async () => {
            setEditTarget(null);
            await reload();
          }}
        />
      ) : null}

      {mergeSource ? (
        <MergeUniversityModal
          source={mergeSource}
          onClose={() => setMergeSource(null)}
          onMerged={async () => {
            setMergeSource(null);
            await reload();
          }}
        />
      ) : null}
    </View>
  );
}

function DirectoryRow({
  row,
  onEdit,
  onDeactivate,
  onReactivate,
  onMerge,
}: {
  row: DirectoryUniversity;
  onEdit: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
  onMerge: () => void;
}) {
  return (
    <View
      style={{
        padding: 12,
        borderRadius: 6,
        backgroundColor: row.is_active ? FOREST_MID : "rgba(255,255,255,0.02)",
        borderWidth: 1,
        borderColor: BORDER,
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 10,
        alignItems: "center",
        opacity: row.is_active ? 1 : 0.65,
      }}
    >
      <View style={{ flex: 1, minWidth: 220 }}>
        <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: LINEN }}>
          {row.name}
          {row.state_code ? (
            <Text style={{ color: WM, fontFamily: FONTS.body }}>  ·  {row.state_code}</Text>
          ) : null}
        </Text>
        <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11.5, color: WS }}>
          {row.primary_email_domain ? `@${row.primary_email_domain}` : "no primary domain"}
          {row.alt_email_domains.length > 0 ? ` · +${row.alt_email_domains.length} alt` : ""}
        </Text>
        {!row.is_active ? <Pill color={RED}>inactive</Pill> : null}
      </View>
      <View style={{ flexDirection: "row", gap: 6 }}>
        <SmallBtn label="Edit" onPress={onEdit} />
        {row.is_active ? (
          <SmallBtn label="Deactivate" onPress={onDeactivate} />
        ) : (
          <SmallBtn label="Reactivate" onPress={onReactivate} tint={SAGE} />
        )}
        <SmallBtn label="Merge…" onPress={onMerge} />
      </View>
    </View>
  );
}

function EditUniversityModal({
  row,
  onClose,
  onSaved,
}: {
  row: DirectoryUniversity;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(row.name);
  const [primary, setPrimary] = useState(row.primary_email_domain ?? "");
  const [altsRaw, setAltsRaw] = useState(row.alt_email_domains.join(", "));
  const [stateCode, setStateCode] = useState(row.state_code ?? "");
  const [website, setWebsite] = useState(row.website_url ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const trimmedState = stateCode.trim().toUpperCase();
      const stateValid = trimmedState === "" || USPS_STATES.includes(trimmedState);
      if (!stateValid) {
        throw new Error(`State must be one of: ${USPS_STATES.join(", ")} or blank.`);
      }
      const alts = altsRaw
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s.length > 0);
      const patch = {
        name: name.trim(),
        primary_email_domain: primary.trim().toLowerCase() || null,
        alt_email_domains: alts,
        state_code: trimmedState || null,
        website_url: website.trim() || null,
      };
      await updateUniversityRow(row.id, patch);
      void logAdminAction({
        action: "university.update",
        entityType: "universities",
        entityId: row.id,
        summary: `Edited ${name.trim()}`,
        diff: { before: row, after: patch },
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <View style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.65)", justifyContent: "center", paddingHorizontal: 16 }}>
        <View style={{ maxWidth: 520, width: "100%", alignSelf: "center", backgroundColor: FOREST_MID, borderRadius: 8, borderWidth: 1, borderColor: BORDER, padding: 20 }}>
          <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>Edit school</Text>
          <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 12, color: WM }}>
            Changes are audit-logged. Setting primary_email_domain to a value already used by another school will fail.
          </Text>
          <DirectoryFieldLabel>Name</DirectoryFieldLabel>
          <TextInput value={name} onChangeText={setName} style={DIRECTORY_INPUT} />
          <DirectoryFieldLabel>Primary email domain</DirectoryFieldLabel>
          <TextInput
            value={primary}
            onChangeText={setPrimary}
            placeholder="example.edu"
            placeholderTextColor={WL}
            autoCapitalize="none"
            style={DIRECTORY_INPUT}
          />
          <DirectoryFieldLabel>Alt domains (comma-separated)</DirectoryFieldLabel>
          <TextInput
            value={altsRaw}
            onChangeText={setAltsRaw}
            placeholder="alumni.example.edu, grad.example.edu"
            placeholderTextColor={WL}
            autoCapitalize="none"
            style={DIRECTORY_INPUT}
          />
          <DirectoryFieldLabel>State code (e.g. FL)</DirectoryFieldLabel>
          <TextInput
            value={stateCode}
            onChangeText={setStateCode}
            placeholder="FL"
            placeholderTextColor={WL}
            autoCapitalize="characters"
            maxLength={2}
            style={DIRECTORY_INPUT}
          />
          <DirectoryFieldLabel>Website URL</DirectoryFieldLabel>
          <TextInput
            value={website}
            onChangeText={setWebsite}
            placeholder="https://www.example.edu"
            placeholderTextColor={WL}
            autoCapitalize="none"
            style={DIRECTORY_INPUT}
          />
          {err ? <ErrorBanner>{err}</ErrorBanner> : null}
          <View style={{ marginTop: 14, flexDirection: "row", gap: 8, justifyContent: "flex-end" }}>
            <SmallBtn label="Cancel" onPress={onClose} />
            <SmallBtn label={busy ? "Saving…" : "Save"} onPress={() => void save()} tint={AMBER} disabled={busy} />
          </View>
        </View>
      </View>
    </Modal>
  );
}

function MergeUniversityModal({
  source,
  onClose,
  onMerged,
}: {
  source: DirectoryUniversity;
  onClose: () => void;
  onMerged: () => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<DirectoryUniversity[]>([]);
  const [targetId, setTargetId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!query.trim()) {
      setResults([]);
      return;
    }
    void listUniversityDirectory(0, { search: query, isActive: true }).then((r) => {
      if (!cancelled) setResults(r.rows.filter((x) => x.id !== source.id).slice(0, 8));
    });
    return () => {
      cancelled = true;
    };
  }, [query, source.id]);

  async function doMerge() {
    if (!targetId) {
      setErr("Pick a target school first.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await mergeUniversityInto(source.id, targetId);
      void logAdminAction({
        action: "university.merge",
        entityType: "universities",
        entityId: source.id,
        summary: `Merged ${source.name} into target ${targetId}`,
        diff: { before: { id: source.id, name: source.name }, after: { merged_into: targetId } },
      });
      onMerged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Merge failed");
    } finally {
      setBusy(false);
    }
  }

  const target = results.find((r) => r.id === targetId) ?? null;

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <View style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.65)", justifyContent: "center", paddingHorizontal: 16 }}>
        <View style={{ maxWidth: 520, width: "100%", alignSelf: "center", backgroundColor: FOREST_MID, borderRadius: 8, borderWidth: 1, borderColor: BORDER, padding: 20 }}>
          <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>
            Merge &quot;{source.name}&quot; into…
          </Text>
          <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 12, color: WM }}>
            The source school&apos;s domains are added to the target&apos;s alt_email_domains, then the source is deactivated. Students already linked keep their FK.
          </Text>
          <TextInput
            value={query}
            onChangeText={setQuery}
            placeholder="Search target school…"
            placeholderTextColor={WL}
            autoFocus
            style={{ ...DIRECTORY_INPUT, marginTop: 12 }}
          />
          <View style={{ marginTop: 8, maxHeight: 280 }}>
            <ScrollView>
              {results.map((r) => {
                const selected = r.id === targetId;
                return (
                  <Pressable
                    key={r.id}
                    onPress={() => setTargetId(r.id)}
                    style={[
                      {
                        paddingHorizontal: 12,
                        paddingVertical: 10,
                        borderRadius: 4,
                        marginBottom: 4,
                        backgroundColor: selected ? "rgba(181,135,58,0.10)" : "transparent",
                        borderWidth: 1,
                        borderColor: selected ? AMBER : BORDER,
                      } as ViewStyle,
                      Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                    ]}
                  >
                    <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: selected ? AMBER : LINEN }}>{r.name}</Text>
                    {r.primary_email_domain ? (
                      <Text style={{ fontFamily: FONTS.body, fontSize: 11, color: WM }}>@{r.primary_email_domain}</Text>
                    ) : null}
                  </Pressable>
                );
              })}
              {query.trim() && results.length === 0 ? (
                <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: WM, padding: 12 }}>
                  No active schools match.
                </Text>
              ) : null}
            </ScrollView>
          </View>
          {err ? <ErrorBanner>{err}</ErrorBanner> : null}
          <View style={{ marginTop: 14, flexDirection: "row", gap: 8, justifyContent: "flex-end" }}>
            <SmallBtn label="Cancel" onPress={onClose} />
            <SmallBtn
              label={busy ? "Merging…" : target ? `Merge into "${target.name}"` : "Merge"}
              onPress={() => void doMerge()}
              tint={AMBER}
              disabled={busy || !targetId}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}

const DIRECTORY_INPUT = {
  marginTop: 6,
  backgroundColor: BLACK,
  borderWidth: 1,
  borderColor: BORDER,
  borderRadius: 4,
  paddingHorizontal: 12,
  paddingVertical: 9,
  color: W,
  fontFamily: FONTS.body,
  fontSize: 13,
  ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
} as unknown as TextStyle;

function DirectoryFieldLabel({ children }: { children: string }) {
  return (
    <Text
      style={{
        marginTop: 12,
        fontFamily: FONTS.bodyMedium,
        fontSize: 10.5,
        letterSpacing: 0.6,
        textTransform: "uppercase",
        color: WM,
      }}
    >
      {children}
    </Text>
  );
}

// ─── Student review row ─────────────────────────────────────────────────

function StudentReviewRow({
  row,
  universities,
  onAction,
}: {
  row: StudentVerificationRow;
  universities: University[];
  onAction: () => void;
}) {
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [pickQuery, setPickQuery] = useState(row.university ?? "");
  const [pickedId, setPickedId] = useState<string | null>(row.university_id);
  const [rejectReason, setRejectReason] = useState("");

  const matchedUni = useMemo(() => {
    if (!pickedId) return null;
    return universities.find((u) => u.id === pickedId) ?? null;
  }, [pickedId, universities]);

  const pickResults = useMemo(() => {
    const q = pickQuery.trim().toLowerCase();
    if (!q) return universities.slice(0, 8);
    return universities.filter((u) => u.name.toLowerCase().includes(q)).slice(0, 12);
  }, [pickQuery, universities]);

  async function onVerify() {
    if (!pickedId) {
      setErr("Pick a school first.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await adminVerifyStudentSchool(row.id, pickedId);
      void logAdminAction({
        action: "student.school_verified",
        entityType: "students",
        entityId: row.id,
        summary: `Manually verified ${row.full_name ?? row.email ?? "student"} → ${matchedUni?.name ?? pickedId}`,
        diff: { after: { university_id: pickedId, school_verified: true, school_verification_method: "manual_admin" } },
      });
      setVerifyOpen(false);
      onAction();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Verify failed");
    } finally {
      setBusy(false);
    }
  }

  async function onReject() {
    if (!rejectReason.trim()) {
      setErr("Reason is required.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await adminRejectStudentSchool(row.id, rejectReason.trim());
      void logAdminAction({
        action: "student.school_rejected",
        entityType: "students",
        entityId: row.id,
        summary: `Rejected school for ${row.full_name ?? row.email ?? "student"}: ${rejectReason.trim()}`,
        diff: { before: { school_verified: row.school_verified } },
      });
      setRejectOpen(false);
      setRejectReason("");
      onAction();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Reject failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <View style={{ padding: 14, borderRadius: 6, backgroundColor: FOREST_MID, borderWidth: 1, borderColor: BORDER }}>
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
        <View style={{ flex: 1, minWidth: 200 }}>
          <Pressable onPress={() => router.push(`/admin/student/${row.id}` as Href)}>
            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13.5, color: LINEN }}>
              {row.full_name ?? "Student"}
            </Text>
          </Pressable>
          <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11.5, color: WS }}>{row.email ?? "—"}</Text>
          <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11, color: WM }}>
            Claimed school: {row.university ?? row.university_name ?? "—"} · Signed up{" "}
            {new Date(row.created_at).toLocaleDateString()}
          </Text>
          <View style={{ marginTop: 4, flexDirection: "row", gap: 4 }}>
            {row.school_verification_method === "self_attested" ? (
              <Pill color={AMBER}>Self-attested</Pill>
            ) : (
              <Pill color={RED}>No school</Pill>
            )}
          </View>
        </View>
        <View style={{ flexDirection: "row", gap: 6 }}>
          <SmallBtn label="✓ Verify" onPress={() => setVerifyOpen(true)} tint={SAGE} />
          <SmallBtn label="✗ Reject" onPress={() => setRejectOpen(true)} tint={RED} />
        </View>
      </View>

      {/* Verify modal */}
      <Modal visible={verifyOpen} transparent animationType="fade" onRequestClose={() => setVerifyOpen(false)}>
        <View style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.65)", justifyContent: "center", paddingHorizontal: 16 }}>
          <View style={{ maxWidth: 520, width: "100%", alignSelf: "center", backgroundColor: FOREST_MID, borderRadius: 8, borderWidth: 1, borderColor: BORDER, padding: 20 }}>
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>Verify school</Text>
            <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 12, color: WM }}>
              Pick the university for {row.full_name ?? row.email ?? "this student"}. Sets verification method to &quot;manual_admin&quot;.
            </Text>
            <TextInput
              value={pickQuery}
              onChangeText={setPickQuery}
              placeholder="Search universities…"
              placeholderTextColor={WL}
              style={{
                marginTop: 12,
                backgroundColor: BLACK,
                borderWidth: 1,
                borderColor: BORDER,
                borderRadius: 4,
                paddingHorizontal: 12,
                paddingVertical: 9,
                color: W,
                fontFamily: FONTS.body,
                fontSize: 13,
                ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
              } as unknown as TextStyle}
              autoFocus
            />
            <View style={{ marginTop: 8, maxHeight: 280 }}>
              <ScrollView>
                {pickResults.map((u) => {
                  const selected = u.id === pickedId;
                  return (
                    <Pressable
                      key={u.id}
                      onPress={() => setPickedId(u.id)}
                      style={[
                        {
                          paddingHorizontal: 12,
                          paddingVertical: 10,
                          borderRadius: 4,
                          marginBottom: 4,
                          backgroundColor: selected ? "rgba(181,135,58,0.10)" : "transparent",
                          borderWidth: 1,
                          borderColor: selected ? AMBER : BORDER,
                        } as ViewStyle,
                        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                      ]}
                    >
                      <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: selected ? AMBER : LINEN }}>{u.name}</Text>
                      {u.primary_email_domain ? (
                        <Text style={{ fontFamily: FONTS.body, fontSize: 11, color: WM }}>@{u.primary_email_domain}</Text>
                      ) : null}
                    </Pressable>
                  );
                })}
                {pickResults.length === 0 ? (
                  <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: WM, padding: 12 }}>
                    No universities match. Maybe approve their request first.
                  </Text>
                ) : null}
              </ScrollView>
            </View>
            {err ? <ErrorBanner>{err}</ErrorBanner> : null}
            <View style={{ marginTop: 14, flexDirection: "row", gap: 8, justifyContent: "flex-end" }}>
              <SmallBtn label="Cancel" onPress={() => { setVerifyOpen(false); setErr(null); }} />
              <SmallBtn label={busy ? "Verifying…" : "Confirm verify"} onPress={() => void onVerify()} tint={AMBER} disabled={busy || !pickedId} />
            </View>
          </View>
        </View>
      </Modal>

      {/* Reject modal */}
      <Modal visible={rejectOpen} transparent animationType="fade" onRequestClose={() => setRejectOpen(false)}>
        <View style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.65)", justifyContent: "center", paddingHorizontal: 16 }}>
          <View style={{ maxWidth: 480, width: "100%", alignSelf: "center", backgroundColor: FOREST_MID, borderRadius: 8, borderWidth: 1, borderColor: BORDER, padding: 20 }}>
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>Reject school</Text>
            <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 12, color: WM }}>
              Clears the student&apos;s school + verification. Their account stays active — they can re-pick from their profile.
            </Text>
            <TextInput
              value={rejectReason}
              onChangeText={setRejectReason}
              placeholder="Why is the claimed school being rejected?"
              placeholderTextColor={WL}
              multiline
              style={{
                marginTop: 12,
                minHeight: 80,
                backgroundColor: BLACK,
                borderWidth: 1,
                borderColor: BORDER,
                borderRadius: 4,
                paddingHorizontal: 12,
                paddingVertical: 10,
                color: W,
                fontFamily: FONTS.body,
                fontSize: 13,
                ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
              } as unknown as TextStyle}
            />
            {err ? <ErrorBanner>{err}</ErrorBanner> : null}
            <View style={{ marginTop: 14, flexDirection: "row", gap: 8, justifyContent: "flex-end" }}>
              <SmallBtn label="Cancel" onPress={() => { setRejectOpen(false); setErr(null); }} />
              <SmallBtn label={busy ? "Rejecting…" : "Confirm reject"} onPress={() => void onReject()} tint={RED} disabled={busy || !rejectReason.trim()} />
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

// ─── University request row ─────────────────────────────────────────────

function UniversityRequestRow({ req, onAction }: { req: UniversityRequest; onAction: () => void }) {
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [primaryDomain, setPrimaryDomain] = useState(req.primary_domain ?? "");
  const [rejectReason, setRejectReason] = useState("");

  async function onApprove() {
    setBusy(true);
    setErr(null);
    try {
      await approveUniversityRequest(req.id, primaryDomain.trim() || null);
      void logAdminAction({
        action: "university_request.approved",
        entityType: "university_requests",
        entityId: req.id,
        summary: `Approved school request: ${req.school_name}`,
      });
      setApproveOpen(false);
      onAction();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Approve failed");
    } finally {
      setBusy(false);
    }
  }

  async function onReject() {
    if (!rejectReason.trim()) {
      setErr("Reason is required.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await rejectUniversityRequest(req.id, rejectReason.trim());
      void logAdminAction({
        action: "university_request.rejected",
        entityType: "university_requests",
        entityId: req.id,
        summary: `Rejected school request: ${req.school_name} — ${rejectReason.trim()}`,
      });
      setRejectOpen(false);
      setRejectReason("");
      onAction();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Reject failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <View style={{ padding: 14, borderRadius: 6, backgroundColor: FOREST_MID, borderWidth: 1, borderColor: BORDER }}>
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
        <View style={{ flex: 1, minWidth: 220 }}>
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13.5, color: LINEN }}>{req.school_name}</Text>
          <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11.5, color: WS }}>
            {req.primary_domain ? `@${req.primary_domain}` : "no domain provided"}
            {req.city || req.state ? ` · ${[req.city, req.state].filter(Boolean).join(", ")}` : ""}
          </Text>
          <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11, color: WM }}>
            Requested by {req.requester_name ?? req.requester_email ?? "—"} · {new Date(req.created_at).toLocaleDateString()}
          </Text>
          {req.notes ? (
            <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 11.5, color: WS, fontStyle: "italic" }}>
              &quot;{req.notes}&quot;
            </Text>
          ) : null}
        </View>
        <View style={{ flexDirection: "row", gap: 6 }}>
          <SmallBtn label="✓ Approve" onPress={() => setApproveOpen(true)} tint={SAGE} />
          <SmallBtn label="✗ Reject" onPress={() => setRejectOpen(true)} tint={RED} />
        </View>
      </View>

      <Modal visible={approveOpen} transparent animationType="fade" onRequestClose={() => setApproveOpen(false)}>
        <View style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.65)", justifyContent: "center", paddingHorizontal: 16 }}>
          <View style={{ maxWidth: 480, width: "100%", alignSelf: "center", backgroundColor: FOREST_MID, borderRadius: 8, borderWidth: 1, borderColor: BORDER, padding: 20 }}>
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>Approve school request</Text>
            <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 12, color: WM }}>
              Creates a new universities row + auto-sets the requester&apos;s school. The domain is also added to the signup whitelist so future students with this domain can sign up.
            </Text>
            <Text style={{ marginTop: 12, fontFamily: FONTS.bodyMedium, fontSize: 10.5, letterSpacing: 0.6, textTransform: "uppercase", color: WM }}>
              Primary domain (optional)
            </Text>
            <TextInput
              value={primaryDomain}
              onChangeText={setPrimaryDomain}
              placeholder="example.edu"
              placeholderTextColor={WL}
              autoCapitalize="none"
              style={{
                marginTop: 6,
                backgroundColor: BLACK,
                borderWidth: 1,
                borderColor: BORDER,
                borderRadius: 4,
                paddingHorizontal: 12,
                paddingVertical: 9,
                color: W,
                fontFamily: FONTS.body,
                fontSize: 13,
                ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
              } as unknown as TextStyle}
            />
            {err ? <ErrorBanner>{err}</ErrorBanner> : null}
            <View style={{ marginTop: 14, flexDirection: "row", gap: 8, justifyContent: "flex-end" }}>
              <SmallBtn label="Cancel" onPress={() => { setApproveOpen(false); setErr(null); }} />
              <SmallBtn label={busy ? "Approving…" : "Confirm approve"} onPress={() => void onApprove()} tint={AMBER} disabled={busy} />
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={rejectOpen} transparent animationType="fade" onRequestClose={() => setRejectOpen(false)}>
        <View style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.65)", justifyContent: "center", paddingHorizontal: 16 }}>
          <View style={{ maxWidth: 480, width: "100%", alignSelf: "center", backgroundColor: FOREST_MID, borderRadius: 8, borderWidth: 1, borderColor: BORDER, padding: 20 }}>
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>Reject school request</Text>
            <TextInput
              value={rejectReason}
              onChangeText={setRejectReason}
              placeholder="Reason (shown to student)"
              placeholderTextColor={WL}
              multiline
              style={{
                marginTop: 12,
                minHeight: 80,
                backgroundColor: BLACK,
                borderWidth: 1,
                borderColor: BORDER,
                borderRadius: 4,
                paddingHorizontal: 12,
                paddingVertical: 10,
                color: W,
                fontFamily: FONTS.body,
                fontSize: 13,
                ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
              } as unknown as TextStyle}
            />
            {err ? <ErrorBanner>{err}</ErrorBanner> : null}
            <View style={{ marginTop: 14, flexDirection: "row", gap: 8, justifyContent: "flex-end" }}>
              <SmallBtn label="Cancel" onPress={() => { setRejectOpen(false); setErr(null); }} />
              <SmallBtn label={busy ? "Rejecting…" : "Confirm reject"} onPress={() => void onReject()} tint={RED} disabled={busy || !rejectReason.trim()} />
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

// ─── Primitives ─────────────────────────────────────────────────────────

function TabButton({ label, active, onPress, badge }: { label: string; active: boolean; onPress: () => void; badge?: number }) {
  return (
    <Pressable
      onPress={onPress}
      style={[
        {
          paddingHorizontal: 14,
          paddingVertical: 10,
          borderBottomWidth: 2,
          borderBottomColor: active ? AMBER : "transparent",
          marginBottom: -1,
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
        } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 11, letterSpacing: 1.4, textTransform: "uppercase", color: active ? AMBER : WS }}>
        {label}
      </Text>
      {typeof badge === "number" && badge > 0 ? (
        <View style={{ minWidth: 18, height: 16, paddingHorizontal: 5, borderRadius: 999, backgroundColor: AMBER, alignItems: "center", justifyContent: "center" }}>
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9.5, color: THEME.pageBg }}>{badge}</Text>
        </View>
      ) : null}
    </Pressable>
  );
}

// These thin wrappers keep every call site in this large file unchanged while
// routing the visuals through the shared brand components.
function FilterChip({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  return <SharedFilterChip theme={THEME} label={label} active={active} onPress={onPress} />;
}

function Pill({ children, color }: { children: string; color: string }) {
  return (
    <View style={{ paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999, borderWidth: 1, borderColor: color, backgroundColor: "rgba(255,255,255,0.02)" }}>
      <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9, letterSpacing: 1.2, textTransform: "uppercase", color }}>{children}</Text>
    </View>
  );
}

function SmallBtn({ label, onPress, tint, disabled }: { label: string; onPress: () => void; tint?: string; disabled?: boolean }) {
  const variant = tint === AMBER ? "filled" : tint === RED ? "destructive" : "outline";
  return <AdminButton theme={THEME} label={label} variant={variant} size="sm" disabled={disabled} onPress={onPress} />;
}

function ErrorBanner({ children }: { children: string }) {
  return (
    <View style={{ marginTop: 12 }}>
      <AdminError theme={THEME} message={children} />
    </View>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <View style={{ marginTop: 20 }}>
      <AdminEmpty theme={THEME} title={title} body={body} />
    </View>
  );
}
